<template>
  <div class="trend-chart-container">
    <!-- 用户搜索栏 -->
    <div class="user-search-bar">
      <el-form :inline="true" class="search-form">
        <el-form-item label="搜索用户:">
          <el-select
              v-model="selectedUserId"
              filterable
              remote
              reserve-keyword
              clearable
              placeholder="请输入用户名或ID进行搜索..."
              :remote-method="searchUsersRemote"
              :loading="userSearchLoading"
              @change="handleUserChange"
              @clear="handleClearUser"
              class="user-select"
          >
            <template #loading>
              <div class="search-loading">
                <el-icon class="is-loading"><Loading /></el-icon>
                <span>搜索中...</span>
              </div>
            </template>
            <template #empty>
              <div class="search-empty">
                <span>{{ selectedUserId ? '未找到匹配用户' : '请输入关键词搜索' }}</span>
              </div>
            </template>
            <el-option
                v-for="user in userOptions"
                :key="user.id"
                :label="user.name"
                :value="user.id"
            >
              <div class="user-option">
                <span class="user-name">{{ user.name.split(' (ID: ')[0] }}</span>
                <span class="user-id">ID: {{ user.id }}</span>
              </div>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button 
            type="info" 
            plain 
            size="small"
            @click="handleClearUser"
            :disabled="!selectedUserId"
          >
            清除
          </el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 图表区域 -->
    <div ref="chartRef" class="chart-view" />
  </div>
</template>

<script setup>
import * as echarts from 'echarts'
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { searchUsers, getUserTrend, getAllNonAdminUserIds } from '@/services/userAnalyzeService'
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'

// Props
const props = defineProps({
  userData: {
    type: Object,
    default: () => null
  },
  title: {
    type: String,
    default: '用户下单次数和登陆次数的趋势分析'
  }
})

// Emits
const emit = defineEmits(['user-change'])

const chartRef = ref(null)
let chartInstance = null
let hasInited = false
let resizeObserver = null

// 用户搜索相关
const selectedUserId = ref('')
const userOptions = ref([])
const userSearchLoading = ref(false)

// 轮播相关
const carouselUserIds = ref([])
const currentCarouselIndex = ref(0)
let carouselTimer = null

// 防抖定时器
let searchDebounceTimer = null

// 格式化月份显示（将 "2024-06" 转换为 "2024年6月"）
const formatMonth = (monthStr) => {
  if (!monthStr) return ''
  try {
    const [year, month] = monthStr.split('-')
    return `${year}年${parseInt(month)}月`
  } catch (error) {
    return monthStr
  }
}

// 清除用户选择
const handleClearUser = () => {
  selectedUserId.value = ''
  userOptions.value = []
  
  // 立即停止轮播
  stopCarousel()
  
  // 清除搜索防抖定时器
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
  
  // 重置图表为初始状态
  if (chartInstance) {
    const option = getTrendOption(null)
    chartInstance.setOption(option)
  }
  
  // 触发事件通知父组件
  emit('user-change', null)
  
  ElMessage.info('已清除用户选择')
}

// 获取所有非管理员用户ID列表
const loadCarouselUserIds = async () => {
  try {
    const result = await getAllNonAdminUserIds()
    if (result.success && result.data) {
      carouselUserIds.value = result.data
      // console.log('轮播用户ID列表加载成功:', carouselUserIds.value.length, '个用户')
      return true
    } else {
      throw new Error(result.error || '获取用户ID列表失败')
    }
  } catch (error) {
    console.error('加载轮播用户ID列表失败:', error)
    ElMessage.error('加载用户列表失败: ' + error.message)
    return false
  }
}

// 开始轮播
const startCarousel = async () => {
  // 如果用户ID列表为空，先加载
  if (carouselUserIds.value.length === 0) {
    const loaded = await loadCarouselUserIds()
    if (!loaded) return
  }

  if (carouselUserIds.value.length === 0) {
    console.warn('没有可轮播的用户数据')
    return
  }

  // 从ID最小的用户开始
  currentCarouselIndex.value = 0

  // 立即显示第一个用户（ID最小的用户）
  await loadCarouselUser()

  // 设置定时器，每30秒切换一次
  carouselTimer = setInterval(async () => {
    currentCarouselIndex.value = (currentCarouselIndex.value + 1) % carouselUserIds.value.length
    await loadCarouselUser()
  }, 30000) // 30秒

  // console.log('轮播已开始，每30秒自动切换用户')
}

// 停止轮播
const stopCarousel = () => {
  if (carouselTimer) {
    clearInterval(carouselTimer)
    carouselTimer = null
  }
}

// 加载当前轮播用户的数据
const loadCarouselUser = async () => {
  if (carouselUserIds.value.length === 0) return

  const userId = carouselUserIds.value[currentCarouselIndex.value]
  try {
    // 获取当前月份（作为结束月份）
    const endMonth = new Date().toISOString().substring(0, 7) + '-01'

    // 调用后端API获取用户趋势数据
    const result = await getUserTrend(Number(userId), endMonth)

    if (result.success && result.data) {
      const trendData = result.data

      // 构建图表数据格式，使用格式化的月份显示
      const userData = {
        id: trendData.userId,
        name: trendData.username || '用户' + trendData.userId,
        loginTrend: trendData.loginTrend || [],
        orderTrend: trendData.orderTrend || [],
        months: (trendData.months || []).map(month => formatMonth(month)), // 格式化月份
        rawMonths: trendData.months || [] // 保留原始月份数据用于其他用途
      }

      // 更新选中的用户ID（但不触发搜索）
      selectedUserId.value = userId.toString()

      // 触发事件通知父组件
      emit('user-change', userData)

      // 更新图表
      updateChart(userData)

      // console.log(`轮播显示用户: ${userData.name} (${currentCarouselIndex.value + 1}/${carouselUserIds.value.length})`)
    } else {
      console.warn(`用户 ${userId} 没有趋势数据，跳过`)
    }
  } catch (error) {
    console.error('轮播加载用户数据失败:', error)
    // 不显示错误消息，避免频繁弹窗
  }
}

const searchUsersRemote = async (query) => {
  if (!query || query.trim().length < 1) { 
    userOptions.value = []
    return 
  }
  
  // 清除之前的定时器
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
  }
  
  // 设置防抖延迟
  searchDebounceTimer = setTimeout(async () => {
    userSearchLoading.value = true
    try {
      const res = await searchUsers(query.trim())
      if (res.success && res.data) {
        userOptions.value = res.data.map(u => ({ 
          id: u.id.toString(), 
          name: `${u.username} (ID: ${u.id})`  // 同时显示用户名和ID
        }))
      } else {
        userOptions.value = []
      }
    } catch (error) {
      console.error('搜索用户失败:', error)
      userOptions.value = []
      ElMessage.error('搜索用户失败')
    } finally {
      userSearchLoading.value = false
    }
  }, 300) // 300ms 防抖延迟
}

const handleUserChange = async (userId) => {
  if (!userId) return
  
  // 手动选择用户时停止轮播
  stopCarousel()
  
  try {
    userSearchLoading.value = true
    
    // 获取当前月份（作为结束月份）
    const endMonth = new Date().toISOString().substring(0, 7) + '-01'
    
    // 调用后端API获取用户趋势数据
    const result = await getUserTrend(Number(userId), endMonth)
    
    if (result.success && result.data) {
      const trendData = result.data
      
      // 构建图表数据格式，使用格式化的月份显示
      const userData = {
        id: trendData.userId,
        name: trendData.username || '用户' + trendData.userId,
        loginTrend: trendData.loginTrend || [],
        orderTrend: trendData.orderTrend || [],
        months: (trendData.months || []).map(month => formatMonth(month)), // 格式化月份
        rawMonths: trendData.months || [] // 保留原始月份数据用于其他用途
      }
      
      // 触发事件通知父组件
      emit('user-change', userData)
      
      // 更新图表
      updateChart(userData)
      
      ElMessage.success(`已加载用户 ${userData.name} 的趋势数据 (${userData.months.length} 个月)`)
    } else {
      throw new Error(result.error || '获取趋势数据失败')
    }
  } catch (error) {
    console.error('获取用户趋势数据失败:', error)
    ElMessage.error('获取用户趋势数据失败: ' + (error.message || '未知错误'))
  } finally {
    userSearchLoading.value = false
  }
}

const getTrendOption = (userData) => {
  // 如果没有用户数据，使用默认的月份数据
  const monthsData = userData?.months && userData.months.length > 0 
    ? userData.months 
    : ['暂无数据']
  
  const loginData = userData?.loginTrend && userData.loginTrend.length > 0 
    ? userData.loginTrend 
    : [0]
  
  const orderData = userData?.orderTrend && userData.orderTrend.length > 0 
    ? userData.orderTrend 
    : [0]

  // 检测是否为移动端
  const isMobile = window.innerWidth <= 768
  
  // 移动端标题文本简化
  const titleText = isMobile 
    ? (userData?.name || '请选择用户')
    : `${props.title} - ${userData?.name || '请选择用户'}`

  return {
    title: {
      text: titleText,
      top: isMobile ? '5px' : '10px',
      left: 'center',
      textStyle: {
        fontSize: isMobile ? 13 : 16,
        fontWeight: 'bold',
        color: '#303133'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      formatter: function(params) {
        let result = `<strong>${params[0].name}</strong><br/>`
        params.forEach(param => {
          result += `${param.seriesName}: ${param.value}<br/>`
        })
        return result
      },
      confine: true // 限制在图表区域内
    },
    legend: {
      data: ['登录次数', '下单次数'],
      top: isMobile ? '30px' : '45px',
      left: 'center',
      itemWidth: isMobile ? 20 : 25,
      itemHeight: isMobile ? 12 : 14,
      textStyle: {
        fontSize: isMobile ? 11 : 12
      }
    },
    grid: {
      left: isMobile ? '8%' : '3%',
      right: isMobile ? '5%' : '4%',
      bottom: isMobile ? '15%' : '8%',
      top: isMobile ? '65px' : '85px',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: monthsData, // 使用动态的月份数据
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: '#E4E7ED'
        }
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#606266',
        fontSize: isMobile ? 8 : 12,
        rotate: isMobile ? 45 : (monthsData.length > 6 ? 45 : 0), // 移动端总是倾斜显示
        interval: 0, // 显示所有标签
        margin: isMobile ? 10 : 8
      }
    },
    yAxis: {
      type: 'value',
      name: '次数',
      nameTextStyle: {
        color: '#606266',
        fontSize: isMobile ? 11 : 12
      },
      nameGap: isMobile ? 8 : 15,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: '#606266',
        fontSize: isMobile ? 10 : 12
      },
      splitLine: {
        lineStyle: {
          color: '#F5F7FA'
        }
      }
    },
    series: [
      {
        name: '登录次数',
        type: 'line',
        data: loginData, // 使用处理过的数据
        smooth: true,
        symbol: 'circle',
        symbolSize: isMobile ? 4 : 6,
        itemStyle: {
          color: '#409EFF',
          borderWidth: isMobile ? 1.5 : 2,
          borderColor: '#fff'
        },
        lineStyle: {
          width: isMobile ? 2 : 3
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(64, 158, 255, 0.3)'
            }, {
              offset: 1, color: 'rgba(64, 158, 255, 0.1)'
            }]
          }
        }
      },
      {
        name: '下单次数',
        type: 'line',
        data: orderData, // 使用处理过的数据
        smooth: true,
        symbol: 'circle',
        symbolSize: isMobile ? 4 : 6,
        itemStyle: {
          color: '#67C23A',
          borderWidth: isMobile ? 1.5 : 2,
          borderColor: '#fff'
        },
        lineStyle: {
          width: isMobile ? 2 : 3
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [{
              offset: 0, color: 'rgba(103, 194, 58, 0.3)'
            }, {
              offset: 1, color: 'rgba(103, 194, 58, 0.1)'
            }]
          }
        }
      }
    ]
  }
}

const initChart = () => {
  if (!chartRef.value) return
  if (hasInited) return
  const el = chartRef.value
  const { clientWidth, clientHeight } = el
  if (!clientWidth || !clientHeight) {
    return
  }

  chartInstance = echarts.init(chartRef.value)
  hasInited = true
  chartInstance.setOption(getTrendOption())
}

const updateChart = (userData) => {
  if (chartInstance) {
    chartInstance.setOption(getTrendOption(userData))
  }
}

const resizeChart = () => {
  if (chartInstance) {
    chartInstance.resize()
  }
}

// 监听外部传入的用户数据变化
watch(() => props.userData, (newData) => {
  if (newData) {
    updateChart(newData)
  }
}, { deep: true })

onMounted(() => {
  // 先初始化图表
  setTimeout(() => {
    initChart()
    
    // 图表初始化完成后，再开始轮播
    setTimeout(() => {
      if (!selectedUserId.value && !props.userData) {
        startCarousel()
      }
    }, 300) // 图表初始化后再等300ms开始轮播，确保图表完全准备好
  }, 500)
  
  if (window.ResizeObserver) {
    resizeObserver = new ResizeObserver(() => {
      if (!hasInited) {
        initChart()
      } else {
        resizeChart()
      }
    })
    if (chartRef.value) resizeObserver.observe(chartRef.value)
  }
  window.addEventListener('resize', resizeChart)
})

onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
    chartInstance = null
  }
  hasInited = false
  
  // 清理轮播定时器
  stopCarousel()
  
  // 清理ResizeObserver
  if (resizeObserver && chartRef.value) {
    try { resizeObserver.unobserve(chartRef.value) } catch(e) { /* empty */ }
    resizeObserver.disconnect()
    resizeObserver = null
  }
  
  // 清理防抖定时器
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
  
  window.removeEventListener('resize', resizeChart)
})

// 暴露方法给父组件
defineExpose({
  resizeChart,
  updateChart,
  handleClearUser
})
</script>

<style scoped>
.trend-chart-container {
  width: 100%;
  height: 100%;
  background-color: #fff;
}

.user-search-bar {
  padding: 16px 0;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 16px;
  background-color: #fff;
  justify-content: center;
  align-items: center;
  display: flex;
  z-index: 10;
}

.search-form {
  width: 100%;
  max-width: 500px;
  display: flex;
  justify-content: center;
}

.user-select {
  width: 300px;
  max-width: 100%;
}

.user-search-bar .el-form-item {
  margin-bottom: 0;
}

.user-search-bar .el-form-item__label {
  font-weight: 500;
  color: #606266;
}

.chart-view {
  width: 100%;
  height: 400px;
  /* 确保图表区域不会覆盖搜索栏 */
  position: relative;
  z-index: 1;
}

/* 增强选择器样式 */
.user-search-bar .el-select {
  min-width: 200px;
}

/* 搜索选项样式 */
.user-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.user-name {
  font-weight: 500;
  color: #303133;
  flex: 1;
}

.user-id {
  font-size: 12px;
  color: #909399;
  background-color: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
}

/* 搜索状态样式 */
.search-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  color: #409EFF;
}

.search-loading .el-icon {
  margin-right: 5px;
}

.search-empty {
  text-align: center;
  padding: 10px;
  color: #909399;
  font-size: 14px;
}

/* 响应式优化 */
@media screen and (max-width: 768px) {
  .user-search-bar {
    padding: 12px 16px;
  }
  
  .search-form {
    max-width: 100%;
  }
  
  .user-search-bar .el-form {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
  
  .user-search-bar .el-form-item {
    margin-bottom: 8px;
    margin-right: 0;
    width: 100%;
  }
  
  .user-search-bar .el-form-item__label {
    font-size: 13px;
    width: 100%;
    text-align: left;
  }
  
  .user-select {
    width: 100% !important;
  }
  
  .user-search-bar .el-select {
    width: 100% !important;
    max-width: 100%;
    min-width: auto;
  }
  
  .user-search-bar .el-button {
    width: 100%;
    margin-top: 8px;
  }
  
  .user-option {
    padding: 5px 0;
  }
  
  .user-name {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
  }
  
  .user-id {
    font-size: 11px;
    white-space: nowrap;
  }
  
  /* 图表区域移动端优化 */
  .chart-view {
    height: 350px;
  }
}
</style>